/**
 * Card art at the printed aspect: 5:7 portrait (744×1039), 7:5 landscape for battlefields (port of
 * runebound.gg `ui/CardImage.tsx`). Takes several sources in priority order (the R2 mirror, then Riot's
 * CDN: `@play/catalog` `artSources`); each gets bounded retries with backoff before the next one, then a
 * named placeholder (`card-art.ts`, batch 3 #27; also tokens without art, owner decision Q46).
 */
import { RefreshCw } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { CardPlaceholder, type CardPlaceholderFace } from './CardPlaceholder';
import { useCardArt } from './card-art';
import { cn } from './cn';

/** Card art with retries, source fallbacks and a placeholder. */
export function CardImage({
  sources,
  alt,
  landscape,
  className,
  sizes,
  priority,
  rounded = 'rounded-[12px]',
  placeholder,
  face,
  large,
  retryable,
  lowRes,
}: {
  /** Image URLs to try in order; empty shows the placeholder. */
  sources: readonly string[];
  alt: string;
  landscape?: boolean | undefined;
  className?: string | undefined;
  sizes?: string | undefined;
  priority?: boolean | undefined;
  rounded?: string | undefined;
  /** What to show without an image (default: a `CardPlaceholder` of `face`, else named by the alt text). */
  placeholder?: ReactNode | undefined;
  /** The card's name, cost, type and Might for the default placeholder. */
  face?: CardPlaceholderFace | undefined;
  /** Bigger placeholder type (preview, inspector). */
  large?: boolean | undefined;
  /** Offer "Retry artwork" on the placeholder when the art failed. */
  retryable?: boolean | undefined;
  /**
   * A smaller image of the same card that is probably cached already (the board's size): shown at once while the
   * large one loads, and kept if the large one fails, so a preview is never an empty box (QoL 2026-10-16).
   */
  lowRes?: string | undefined;
}) {
  const art = useCardArt(sources);
  const [lowFailed, setLowFailed] = useState(false);
  const low = lowRes && !lowFailed ? lowRes : null;
  const showPlaceholder = (art.failed && low === null) || art.none;
  return (
    <div
      className={cn(
        'relative overflow-hidden bg-surface2',
        rounded,
        landscape ? 'aspect-[1039/744]' : 'aspect-[744/1039]',
        className,
      )}
      data-art={art.failed ? 'failed' : art.none ? 'none' : art.loaded ? 'loaded' : 'loading'}
    >
      {art.src ? (
        <img
          key={art.tryKey}
          src={art.src}
          alt={alt}
          sizes={sizes}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={art.onLoad}
          onError={art.onError}
          className={cn(
            'absolute inset-0 w-full h-full object-cover transition-opacity duration-300',
            art.loaded ? 'opacity-100' : 'opacity-0',
          )}
        />
      ) : null}
      {showPlaceholder ? (
        <>
          {placeholder ?? <CardPlaceholder face={face ?? { name: alt || 'Card' }} large={large} failed={art.failed} />}
          {art.failed && retryable ? (
            <RetryArtwork onRetry={art.retry} className="absolute bottom-2 left-1/2 -translate-x-1/2" />
          ) : null}
        </>
      ) : null}
      {!art.loaded && !art.none && low !== null ? (
        <img
          src={low}
          alt=""
          aria-hidden
          decoding="async"
          onError={() => {
            setLowFailed(true);
          }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : null}
      {!showPlaceholder && !art.loaded && low === null ? (
        <div className="absolute inset-0 animate-pulse bg-panel3/60" />
      ) : null}
    </div>
  );
}

/** The small "Retry artwork" action shown where card art failed. */
export function RetryArtwork({ onRetry, className }: { onRetry: () => void; className?: string | undefined }) {
  return (
    <button
      type="button"
      className={cn(
        'z-[1] inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[12px] font-medium whitespace-nowrap',
        'bg-[#0c121a]/90 text-muted hover:text-text shadow-[inset_0_0_0_1px_#293442] transition-colors pointer-events-auto',
        'pointer-coarse:h-11 pointer-coarse:px-3.5',
        className,
      )}
      onClick={(e) => {
        e.stopPropagation();
        onRetry();
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
      }}
      data-testid="retry-artwork"
    >
      <RefreshCw size={12} aria-hidden />
      Retry artwork
    </button>
  );
}
